import { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  Select,
  Table,
  Row,
  Col,
  Switch,
  notification,
} from "antd";
import moment from "moment";
import dayjs from "dayjs/esm";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

import "./styles.css";

import { Context } from "../../context";
import { Fetch } from "../../fetch";
import { convertIdToKey, handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";

const { Column } = Table;
const dateFormat = "YYYY-MM-DD HH:mm:ss";

dayjs.extend(weekday);
dayjs.extend(localeData);

const ManageTickets = () => {
  const { token, apiUrl } = useContext(Context);

  const [tickets, setTickets] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    start_date: dayjs()
      .subtract(0, "month")
      .startOf("month")
      .format(dateFormat),
    end_date: dayjs().subtract(0, "month").endOf("month").format(dateFormat),
    content_id: null,
    paid: true,
    currency: null,
  });
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);

  const handleTableDataChange = (page) => {
    let f = filters;
    let moviestring = f.content_id ? `content_id=${f.content_id}&` : ``;
    let starting_date = filters.start_date;
    let ending_date = filters.end_date;
    let sdate = moment(starting_date).isValid()
      ? moment(starting_date).format(dateFormat)
      : null;
    let edate = moment(ending_date).isValid()
      ? moment(ending_date).format(dateFormat)
      : null;
    let datestring =
      sdate && edate ? `start_date=${sdate}&end_date=${edate}` : ``;
    let paidstring = f.paid ? `paid=${true}` : `paid=${false}`;
    let currencystring = f.currency ? `currency=${f.currency}` : ``;
    let fstring = f.content_id ? `${moviestring}${datestring}` : datestring;
    setQuery(fstring);
    Fetch(
      `${apiUrl}/tickets?current=${page.current}&size=${page.pageSize}&${fstring}&${paidstring}&${currencystring}`,
      token,
      (result) => {
        const tickets = convertIdToKey(result.tickets);
        setTickets(tickets);
        setPagination((prev) => {
          return {
            ...page,
            total: result.total,
          };
        });
      }
    );
  };

  useEffect(() => {
    handleTableDataChange(pagination);
  }, [apiUrl, token, filters]);

  useEffect(() => {
    if (movies.length === 0) {
      Fetch(`${apiUrl}/movies`, token, (result) => {
        const movies = convertIdToKey(result.movies);
        setMovies(movies);
      });
    }

    if (series.length === 0) {
      Fetch(`${apiUrl}/series`, token, (result) => {
        const series = convertIdToKey(result.series);
        setSeries(series);
      });
    }
  }, []);

  const recheckPayment = (id) => {
    setLoading(true);

    Fetch(`${apiUrl}/tickets/${id}`, token, (result) => {
      if (result.paid) {
        notification.success({
          message: "Transaction Successful",
          description: "Order Successfully updated",
        });
        setTickets(
          tickets.map((t) => (t.key === id ? { ...t, paid: true } : t))
        );
      } else {
        notification.info({
          message: "Transaction Failed",
          description: "For more details check the PG Dashboard",
        });
      }
      setLoading(false);
    });
  };

  const onChangeDate = (date, datestring) => {
    const [start_date, end_date] = [datestring[0], datestring[1]];
    const getStartOfDay = (start_date) => {
      const startOfDay = moment(start_date).startOf("day").format(dateFormat);
      return startOfDay;
    };
    const getEndOfDay = (end_date) => {
      const endOfDay = moment(end_date).endOf("day").format(dateFormat);
      return endOfDay;
    };
    setFilters({
      ...filters,
      start_date: start_date ? getStartOfDay(start_date) : null,
      end_date: end_date ? getEndOfDay(end_date) : null,
    });
  };

  const customRanges = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last Week": [dayjs().subtract(7, "day"), dayjs()],
  };

  return (
    <div className="manage-orders-wrapper">
      <Row style={{ marginBottom: "20px" }}>
        <Col span={2}>
          {" "}
          Paid{" "}
          <Switch
            checked={filters.paid ? true : false}
            onChange={(checked) =>
              setFilters({ ...filters, paid: checked ? true : null })
            }
          />
        </Col>
        <Col span={4}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by currency"}
            allowClear
            onChange={(value) => setFilters({ ...filters, currency: value })}
          >
            {["INR", "EUR", "USD", "SGD", "AED", "NPR", "MPR"].map((code) => (
              <Select.Option key={code} value={code}>
                {code}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by Content"}
            showSearch
            value={filters.content_id}
            allowClear
            onChange={(value) => setFilters({ ...filters, content_id: value })}
          >
            {movies
              .filter((doc) => doc.model === "ticket")
              .map((doc) => (
                <Select.Option key={doc.key} value={doc.key}>
                  {doc.title}{" "}
                </Select.Option>
              ))}
            {series
              .filter((doc) => doc.model === "ticket")
              .map((doc) => (
                <Select.Option key={doc.key} value={doc.key}>
                  {doc.title}{" "}
                </Select.Option>
              ))}
          </Select>{" "}
        </Col>
        <Col span={1}></Col>
        <Col span={6}>
          <DatePicker.RangePicker
            ranges={customRanges}
            onChange={onChangeDate}
            defaultValue={[
              dayjs().subtract(0, "month").startOf("month"),
              dayjs().subtract(0, "month").endOf("month"),
            ]}
          />
        </Col>
      </Row>
      <Table
        dataSource={tickets}
        pagination={pagination}
        onChange={handleTableDataChange}
      >
        <Column title="ID" dataIndex="key" key="key" align="center" />
        <Column title="User" dataIndex="user" key="language" align="center" />
        <Column
          title="Date"
          dataIndex="date"
          key="date"
          align="center"
          render={(_, record) => moment(record.date).format(dateFormat)}
        />
        <Column
          title="End"
          dataIndex="end"
          key="end"
          align="center"
          render={(_, record) =>
            moment(record.date).add(record.streamPeriod, "days").fromNow()
          }
        />

        <Column
          title="Title"
          key="title"
          align="center"
          render={(text, record) => record?.name || record?.bundle_name}
        />
        
        <Column
          title="Amount"
          dataIndex="amount"
          key="amount"
          align="center"
          render={(_, record) => `${record.currency} ${record.amount}`}
        />
        <Column
          title="Paid"
          dataIndex="paid"
          key="paid"
          align="center"
          onFilter={(value, record) => record.paid === value}
          render={(paid) =>
            paid ? <CheckCircleFilled /> : <CloseCircleOutlined />
          }
        />
        <Column
          title="Actions"
          align="center"
          render={(_, record) =>
            record.paid ? (
              "-"
            ) : (
              <a onClick={() => recheckPayment(record.key)} disabled={loading}>
                Recheck Status
              </a>
            )
          }
        />
      </Table>
      <ExportButton
        className="export-btn-only"
        onExport={() =>
          handleExportClick(apiUrl, token, `tickets?${query}`, "Tickets.csv")
        }
      />
    </div>
  );
};

export default ManageTickets;
